import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import FiltersList from "../../../Common/FiltersList";

import { SearchText, Checkbox } from "../../../../core/inputs";
import { ItemsCounter, Dropdown } from "../../../../core";
import { Colors } from "../../../../core/utils";
import { ButtonTooltip } from "../../../../core/compose";

import { ModalTypes } from "../Utils";

import { useTable } from "../../../../redux/Table/hooks";
import { updateWholeListOption } from "../../../../redux/Table/actions";

function ArchivedStudentListTableHeader({
  filters,
  onChange,
  onRemove,
  openModal,
  organizationId,
  archivedListsState,
  filterTagsListType,
  archiveStudentListSearch,
  selectedArchiveStudentLists,
}) {
  const dispatch = useDispatch();

  const { wholeList } = useTable();

  const [tooltipOptions, setTooltipOptions] = useState({});

  useEffect(() => {
    if (archivedListsState.itemsCount <= 0) {
      setTooltipOptions({
        text: `This is the total number of lists you have archived. You can only archive or delete lists that you own.`,
        open: false,
        onClose: null,
      });
    }
  }, [archivedListsState.itemsCount, tooltipOptions.open]);

  const getActionLabel = (count) => {
    return count === 1 ? ` ${count} List` : ` ${count} Lists`;
  };

  const handleOnSelectOption = (data) => data.handlerOnClick();

  const dropdownOptions = [
    {
      id: "delete",
      enabled: selectedArchiveStudentLists.length > 0 || wholeList,
      handlerOnClick: () => {
        const data = {
          filters,
          organizationId,
          isArchived: true,
          deleteLists: !wholeList
            ? selectedArchiveStudentLists.length
            : archivedListsState.itemsCount,
          lists: !wholeList ? selectedArchiveStudentLists : null,
        };
        openModal(data, ModalTypes.DELETE_LIST);
      },
      label: (
        <div className="text-center text-primary-blue">
          Delete
          {getActionLabel(
            wholeList
              ? archivedListsState?.itemsCount
              : selectedArchiveStudentLists.length
          )}
        </div>
      ),
    },
    {
      id: "unarchive",
      enabled: selectedArchiveStudentLists.length > 0 || wholeList,
      handlerOnClick: () => {
        const data = {
          filters,
          organizationId,
          unarchiveLists: !wholeList
            ? selectedArchiveStudentLists.length
            : archivedListsState.itemsCount,
          lists: !wholeList ? selectedArchiveStudentLists : null,
        };
        openModal(data, ModalTypes.UNARCHIVE_LIST);
      },
      label: (
        <div className="text-center text-primary-blue">
          Unarchive
          {getActionLabel(
            wholeList
              ? archivedListsState?.itemsCount
              : selectedArchiveStudentLists.length
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="pb-2 flex flex-1 text-sm flex-row items-center">
        <div className="flex flex-1 flex-row">
          <div className="flex flex-col">
            <div className="text-4xl font-bold">Archived Student Lists</div>
          </div>
        </div>
        <div className="flex flex-1 flex-row space-x-4 justify-end">
          <div className="min-w-15rem font-bold">
            <Dropdown
              color={Colors.BLUE}
              defaultLabel="Action"
              options={dropdownOptions}
              handleOnSelect={handleOnSelectOption}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 text-sm flex-row justify-start">
        <div className="flex w-1/2 flex-row justify-start">
          <SearchText
            handlerOnSubmit={onChange}
            id="archived-student-lists-search-bar"
            defaultValue={archiveStudentListSearch}
            placeholder="Archived Student Lists Search"
          />
        </div>
      </div>
      <div className="mt-2 flex flex-1 flex-row space-between">
        <FiltersList
          onRemove={onRemove}
          filters={filterTagsListType}
          isLoading={archivedListsState.isLoading}
        />
      </div>
      <div className="mt-4 flex text-sm flex-row items-center">
        <ItemsCounter
          pluralDescriptionItems={
            selectedArchiveStudentLists.length > 0 || wholeList
              ? "Archived Lists Selected"
              : "Archived Lists"
          }
          singularDescriptionItems={
            selectedArchiveStudentLists.length > 0 || wholeList
              ? "Archived List Selected"
              : "Archived List"
          }
          numberItems={
            !wholeList && selectedArchiveStudentLists.length > 0
              ? selectedArchiveStudentLists.length
              : archivedListsState?.itemsCount || 0
          }
        />
        <ButtonTooltip
          questionSize={20}
          tooltipColor={Colors.BLUE}
          tooltipCloseButton={true}
          tooltipDirection={"TOP-LEFT"}
          tooltipClassName="-left-2 top-16"
          tooltipCloseOnClickOutside={true}
          tooltipOptions={tooltipOptions}
        />
        {selectedArchiveStudentLists.length > 0 &&
          archivedListsState.itemsCount !==
            selectedArchiveStudentLists.length && (
            <div className="w-fit">
              <Checkbox
                value={wholeList}
                checked={wholeList}
                color={Colors.BLUE}
                label={`Select All (${archivedListsState?.itemsCount} Lists)`}
                onChange={() => {
                  dispatch(updateWholeListOption(!wholeList));
                }}
              />
            </div>
          )}
      </div>
    </>
  );
}

ArchivedStudentListTableHeader.propTypes = {
  openModal: PropTypes.func,
  filters: PropTypes.shape(),
  organizationId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  archiveListsState: PropTypes.shape(),
  filterTagsListType: PropTypes.array.isRequired,
  archiveStudentListSearch: PropTypes.string.isRequired,
  selectedArchiveStudentLists: PropTypes.arrayOf(PropTypes.PropTypes.shape())
    .isRequired,
};

export default ArchivedStudentListTableHeader;
